import { camelCase, get, isDate, isNil } from 'lodash';
import { useI18n } from 'vue-i18n';

import {
  arr,
  AssetContainerEventType,
  getAssetContainerStatus as coreGetAssetContainerStatus,
  getAssetContainerStatusBeforeTransit
} from '@nova/core';
import {
  getAssetContainerStatusColor as coreGetAssetContainerStatusColor,
  getAssetContainerStatusIcon as coreGetAssetContainerStatusIcon
} from '@satellite/helpers/yardTaskUtils';

import type { IAssetContainerEvent, Maybe } from '@nova/core';
import type { YardAssetDetails } from '@satellite/composables/yardAssetPresenterShared';

/** Minimal event shape the status rules need; list rows and DTOs carry slimmer events. */
export interface YardAssetStatusEvent {
  eventType?: Maybe<string>;
  createDateTime?: Maybe<string | Date>;
}

/** An asset container, or just its events: either is enough to resolve a status. */
export type YardAssetStatusInput = Maybe<
  | YardAssetDetails
  | YardAssetStatusEvent[]
  | { assetContainerEvents?: Maybe<YardAssetStatusEvent[]> }
>;

export function toAssetContainerEvents(input: YardAssetStatusInput): IAssetContainerEvent[] {
  const events = Array.isArray(input) ? input : get(input, 'assetContainerEvents');

  return arr.toArray(events) as IAssetContainerEvent[];
}

export function getRawAssetContainerStatus(input: YardAssetStatusInput): string | null {
  return coreGetAssetContainerStatus(toAssetContainerEvents(input));
}

/**
 * Epoch millis for an event timestamp, or `-Infinity` when it is missing or unparseable, so an
 * event with no usable timestamp never wins the "latest" reduce.
 *
 * Parsing rather than comparing raw values, because the field takes both `Date` and ISO strings and
 * `>` compares those two by neither: `Date > string` coerces the string to `NaN`, and an
 * offset-bearing ISO string sorts lexicographically against a `Z` one instead of chronologically.
 */
function toEpochMillis(value: Maybe<string | Date>): number {
  if (isNil(value)) {
    return Number.NEGATIVE_INFINITY;
  }

  const millis = isDate(value) ? value.getTime() : Date.parse(value);

  return Number.isNaN(millis) ? Number.NEGATIVE_INFINITY : millis;
}

function latestEventByCreateDateTime(events: YardAssetStatusEvent[]): YardAssetStatusEvent | null {
  if (!events.length) {
    return null;
  }

  // Ties resolve to the later element, matching core's `getAssetContainerStatus` reduce.
  return events.reduce((prev, current) =>
    toEpochMillis(prev.createDateTime) > toEpochMillis(current.createDateTime) ? prev : current
  );
}

/** In Transit is a condition, not a status: the asset keeps the work status it had when it started. */
export function getAssetContainerDisplayStatus(input: YardAssetStatusInput): string | null {
  const events = toAssetContainerEvents(input);
  const rawStatus = coreGetAssetContainerStatus(events);

  if (rawStatus !== AssetContainerEventType.InTransit) {
    return rawStatus;
  }

  return getAssetContainerStatusBeforeTransit(events);
}

/**
 * The event behind {@link getAssetContainerDisplayStatus} — not necessarily the newest one, since
 * In Transit is skipped over.
 *
 * Exists so a caller rendering *when* the status was set reads the event that produced the label it
 * annotates. Picking the newest event independently would contradict the label on every asset in
 * transit. Derived from the displayed status rather than re-deriving the rules, so the two cannot
 * drift apart.
 *
 * `null` when no event backs the displayed status: there are no events, or the status is the
 * synthetic `Arrived` that {@link getAssetContainerStatusBeforeTransit} falls back to when every
 * event is one In Transit skips over.
 */
export function getAssetContainerDisplayStatusEvent(
  input: YardAssetStatusInput
): YardAssetStatusEvent | null {
  const events = toAssetContainerEvents(input) as YardAssetStatusEvent[];
  const displayStatus = getAssetContainerDisplayStatus(events);

  if (!displayStatus) {
    return null;
  }

  return latestEventByCreateDateTime(events.filter(event => event?.eventType === displayStatus));
}

/**
 * Single source of truth for an asset container's status: the raw status, the status to display,
 * the in-transit condition, and the label, icon and colour of each.
 */
export function useYardAssetStatus() {
  const { t } = useI18n();

  const getRawStatus = (asset: YardAssetStatusInput): string | null =>
    getRawAssetContainerStatus(asset);

  const getDisplayStatus = (asset: YardAssetStatusInput): string | null =>
    getAssetContainerDisplayStatus(asset);

  /** The event behind {@link getDisplayStatus}, for callers rendering when the status was set. */
  const getDisplayStatusEvent = (asset: YardAssetStatusInput): YardAssetStatusEvent | null =>
    getAssetContainerDisplayStatusEvent(asset);

  const isAssetInTransit = (asset: YardAssetStatusInput): boolean =>
    getRawStatus(asset) === AssetContainerEventType.InTransit;

  const isAssetMissing = (asset: YardAssetStatusInput): boolean =>
    getRawStatus(asset) === AssetContainerEventType.Missing;

  const getStatusLabel = (status: Maybe<string>): string | null => {
    if (!status) {
      return null;
    }

    return t(`core.yard.assetContainerEvent.types.${camelCase(status)}`, status);
  };

  const getRawStatusLabel = (asset: YardAssetStatusInput): string | null =>
    getStatusLabel(getRawStatus(asset));

  const getDisplayStatusLabel = (asset: YardAssetStatusInput): string | null =>
    getStatusLabel(getDisplayStatus(asset));

  const getRawStatusIcon = (asset: YardAssetStatusInput): string =>
    coreGetAssetContainerStatusIcon(getRawStatus(asset) ?? '');

  const getDisplayStatusIcon = (asset: YardAssetStatusInput): string =>
    coreGetAssetContainerStatusIcon(getDisplayStatus(asset) ?? '');

  const getRawStatusColor = (asset: YardAssetStatusInput): string | undefined =>
    coreGetAssetContainerStatusColor(getRawStatus(asset) ?? '');

  const getDisplayStatusColor = (asset: YardAssetStatusInput): string | undefined =>
    coreGetAssetContainerStatusColor(getDisplayStatus(asset) ?? '');

  return {
    getRawStatus,
    getDisplayStatus,
    getDisplayStatusEvent,
    isAssetInTransit,
    isAssetMissing,
    getStatusLabel,
    getRawStatusLabel,
    getDisplayStatusLabel,
    getRawStatusIcon,
    getDisplayStatusIcon,
    getRawStatusColor,
    getDisplayStatusColor
  };
}
